
export interface SplitServiceItem {
  // Core identifiers
  id: string;                      // UUID của item này
  splitGroupId?: string;           // UUID chung cho nhóm split (để group lại)
  splitIndex?: number;             // Index trong group (1, 2, 3...)

  // Edit tracking
  isManuallyEdited?: boolean;      // Flag đánh dấu đã edit thủ công
  editedFields?: string[];         // Array các fields đã bị edit thủ công

  // Service data (from existing structure)
  code: string;
  name: string;
  classId?: number;
  parentId?: string;

  attributes: {
    svcQuantity: number;           // Luôn = 1 cho split items
    svcFee?: number;
    svcFeeHospital?: number;
    svcFeeIns?: number;
    svcPrice?: number;
    deptLaboratoryName?: string;
    [key: string]: any;
  };

  // Editable fields
  visitObjectType?: { code: string; name: string };
  fulfillDeptCode?: string;
  fulfillDeptName?: string;
  samplingDeptCode?: string;
  samplingDeptName?: string;
  notes?: string;
  isOwnExpenses?: boolean;
  obs?: any;                       // Additional info

  // Other fields
  [key: string]: any;
}

/**
 * Merged row data - Dùng để hiển thị grouped view
 */
export interface MergedServiceRow {
  // Identifiers
  id: string;                      // ID của merged row (= splitGroupId)
  isMergedRow: true;               // Flag để phân biệt merged vs normal row
  isExpanded?: boolean;            // Track expand state

  // Split info
  splitGroupId: string;
  splitCount: number;              // Số lượng split items
  splitItems: SplitServiceItem[];  // Array các split items

  // Display data (from first item or aggregated)
  code: string;
  name: string;
  totalQuantity: number;           // = splitItems.length

  // Editable fields - hiển thị giá trị chung hoặc "Nhiều giá trị"
  visitObjectType?: { code: string; name: string } | null;
  fulfillDeptName?: string;
  samplingDeptName?: string;
  notes?: string;
  isOwnExpenses?: boolean;

  // Aggregated data
  totalPrice?: number;             // Tổng thành tiền
  hasMultipleValues?: {            // Track fields có nhiều giá trị khác nhau
    visitObjectType?: boolean;
    fulfillDept?: boolean;
    samplingDept?: boolean;
    notes?: boolean;
    isOwnExpenses?: boolean;
  };

  [key: string]: any;
}

// ==================== HELPER CLASS ====================

export class ServiceSplitHelper {

  /**
   * Generate UUID (using crypto.randomUUID or fallback)
   */
  static generateUUID(): string {
    // Use crypto.randomUUID if available (modern browsers)
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    // Fallback: Generate UUID v4 manually
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Kiểm tra xem có nên auto-split không
   * Giới hạn quantity <= 15
   */
  static shouldAutoSplit(service: any, quantity: number, isEnabled: boolean): boolean {
    return isEnabled && quantity > 1 && quantity <= 15 && !service.splitGroupId;
  }

  /**
   * Tạo split items từ service có quantity > 1
   *
   * @param service - Service gốc
   * @param quantity - Số lượng cần split
   * @returns Array các split items
   */
  static createSplitItems(service: any, quantity: number): SplitServiceItem[] {
    const splitGroupId = this.generateUUID();
    const originalServiceId = service.id; // Preserve original service ID for termId mapping

    return Array.from({ length: quantity }, (_, index) => ({
      ...service,
      id: originalServiceId, // Keep original service ID (needed for termId mapping to backend)
      _clientId: this.generateUUID(), // Add client-side tracking ID for React keys
      splitGroupId,
      splitIndex: index + 1,
      attributes: {
        ...service.attributes,
        svcQuantity: 1, // Mỗi item quantity = 1
      },
      isManuallyEdited: false,
      editedFields: [],
    }));
  }

  /**
   * Group orders by splitGroupId
   */
  private static groupBySplitGroupId(orders: SplitServiceItem[]): Map<string, SplitServiceItem[]> {
    const grouped = new Map<string, SplitServiceItem[]>();

    orders.forEach(order => {
      if (order.splitGroupId) {
        const group = grouped.get(order.splitGroupId) || [];
        group.push(order);
        grouped.set(order.splitGroupId, group);
      }
    });

    return grouped;
  }

  /**
   * Kiểm tra xem field có nhiều giá trị khác nhau không
   */
  private static hasMultipleValues(items: SplitServiceItem[], field: string): boolean {
    if (items.length <= 1) return false;

    const values = items.map(item => {
      if (field.includes('.')) {
        // Nested field (e.g., "visitObjectType.code")
        const parts = field.split('.');
        let value = item;
        for (const part of parts) {
          value = value?.[part];
        }
        return value;
      }
      return item[field];
    });

    const uniqueValues = new Set(values.map(v => JSON.stringify(v)));
    return uniqueValues.size > 1;
  }

  /**
   * Tạo merged row từ split items
   */
  private static createMergedRow(groupId: string, items: SplitServiceItem[]): MergedServiceRow {
    const firstItem = items[0];
    const sortedItems = items.sort((a, b) => (a.splitIndex || 0) - (b.splitIndex || 0));

    // Calculate total price
    const totalPrice = items.reduce((sum, item) => {
      const unitPrice = item.attributes?.svcPrice || 0;
      return sum + unitPrice;
    }, 0);

    // Check for multiple values
    const hasMultipleValues = {
      visitObjectType: this.hasMultipleValues(items, 'visitObjectType.code'),
      fulfillDept: this.hasMultipleValues(items, 'fulfillDeptCode'),
      samplingDept: this.hasMultipleValues(items, 'samplingDeptCode'),
      notes: this.hasMultipleValues(items, 'notes'),
      isOwnExpenses: this.hasMultipleValues(items, 'isOwnExpenses'),
    };

    return {
      id: groupId,
      isMergedRow: true,
      isExpanded: false,
      splitGroupId: groupId,
      splitCount: items.length,
      splitItems: sortedItems,
      code: firstItem.code,
      name: firstItem.name,
      totalQuantity: items.length,
      visitObjectType: hasMultipleValues.visitObjectType ? null : firstItem.visitObjectType,
      fulfillDeptName: hasMultipleValues.fulfillDept ? 'Nhiều phòng' : firstItem.fulfillDeptName,
      samplingDeptName: hasMultipleValues.samplingDept ? 'Nhiều phòng' : firstItem.samplingDeptName,
      notes: hasMultipleValues.notes ? 'Nhiều ghi chú' : firstItem.notes,
      isOwnExpenses: hasMultipleValues.isOwnExpenses ? undefined : firstItem.isOwnExpenses,
      totalPrice,
      hasMultipleValues,
      // Copy other fields from first item
      classId: firstItem.classId,
      parentId: firstItem.parentId,
      attributes: firstItem.attributes,
    };
  }

  /**
   * Merge split items thành merged rows để hiển thị
   *
   * @param orders - Array orders (có thể có split items)
   * @param shouldMerge - Flag để bật/tắt merge (default: true)
   * @returns Array gồm normal items và merged rows
   */
  static mergeOrdersForDisplay(
    orders: SplitServiceItem[],
    shouldMerge: boolean = true
  ): Array<SplitServiceItem | MergedServiceRow> {
    if (!shouldMerge) return orders;

    const grouped = this.groupBySplitGroupId(orders);
    const result: Array<SplitServiceItem | MergedServiceRow> = [];
    const processedGroupIds = new Set<string>();

    console.log('mergeOrdersForDisplay - Input:', {
      ordersCount: orders.length,
      groupsCount: grouped.size,
      groups: Array.from(grouped.entries()).map(([id, items]) => ({
        groupId: id,
        itemsCount: items.length,
      })),
    });

    orders.forEach(order => {
      if (order.splitGroupId && !processedGroupIds.has(order.splitGroupId)) {
        // Tìm tất cả items trong group
        const groupItems = grouped.get(order.splitGroupId) || [];

        if (groupItems.length > 1) {
          // Có nhiều items → tạo merged row
          const mergedRow = this.createMergedRow(order.splitGroupId, groupItems);
          console.log('mergeOrdersForDisplay - Created merged row:', {
            groupId: order.splitGroupId,
            itemsCount: groupItems.length,
            mergedRow,
          });
          result.push(mergedRow);
          processedGroupIds.add(order.splitGroupId);
        } else {
          // Chỉ có 1 item → hiển thị bình thường
          result.push(order);
        }
      } else if (!order.splitGroupId) {
        // Không phải split item → hiển thị bình thường
        result.push(order);
      }
    });

    console.log('mergeOrdersForDisplay - Result:', {
      resultCount: result.length,
      mergedRowsCount: result.filter((r: any) => r.isMergedRow).length,
    });

    return result;
  }

  /**
   * Expand merged row để lấy split items
   */
  static expandMergedRow(mergedRow: MergedServiceRow): SplitServiceItem[] {
    return mergedRow.splitItems;
  }

  /**
   * Check xem có phải merged row không
   */
  static isMergedRow(row: any): row is MergedServiceRow {
    return row && row.isMergedRow === true;
  }

  /**
   * Update field của parent → apply to children chưa bị edit
   *
   * @param splitGroupId - ID của group cần update
   * @param field - Field name
   * @param value - Giá trị mới
   * @param orders - Array orders hiện tại
   * @returns Array orders đã update
   */
  static updateParentField(
    splitGroupId: string,
    field: string,
    value: any,
    orders: SplitServiceItem[]
  ): SplitServiceItem[] {
    return orders.map(item => {
      if (item.splitGroupId === splitGroupId) {
        // Chỉ update nếu field chưa bị edit thủ công
        if (!item.editedFields?.includes(field)) {
          return {
            ...item,
            [field]: value,
          };
        }
      }
      return item;
    });
  }

  /**
   * Mark field as manually edited
   *
   * @param itemId - ID của item
   * @param field - Field đã bị edit
   * @param orders - Array orders
   * @returns Array orders đã update
   */
  static markFieldAsEdited(
    itemId: string,
    field: string,
    orders: SplitServiceItem[]
  ): SplitServiceItem[] {
    return orders.map(item => {
      if (item.id === itemId) {
        const editedFields = item.editedFields || [];
        if (!editedFields.includes(field)) {
          return {
            ...item,
            isManuallyEdited: true,
            editedFields: [...editedFields, field],
          };
        }
      }
      return item;
    });
  }

  /**
   * Adjust quantity (add/remove split items)
   *
   * @param splitGroupId - ID của group
   * @param newQuantity - Số lượng mới
   * @param orders - Array orders hiện tại
   * @returns Array orders đã adjust
   */
  static adjustSplitQuantity(
    splitGroupId: string,
    newQuantity: number,
    orders: SplitServiceItem[]
  ): SplitServiceItem[] {
    const groupItems = orders.filter(item => item.splitGroupId === splitGroupId);
    const currentQuantity = groupItems.length;

    if (newQuantity === currentQuantity || newQuantity < 1) {
      return orders;
    }

    if (newQuantity > currentQuantity) {
      // Thêm items - clone từ item đầu tiên (chưa bị edit)
      const template = groupItems.find(item => !item.isManuallyEdited) || groupItems[0];
      const newItems = this.createSplitItems(template, newQuantity - currentQuantity)
        .map(item => ({
          ...item,
          splitGroupId, // Giữ nguyên groupId
        }));

      return [...orders, ...newItems];
    } else {
      // Xóa items - xóa từ cuối, ưu tiên xóa items chưa bị edit
      const sortedItems = [...groupItems].sort((a, b) => {
        // Xóa items chưa edit trước
        if (a.isManuallyEdited && !b.isManuallyEdited) return 1;
        if (!a.isManuallyEdited && b.isManuallyEdited) return -1;
        // Nếu cùng trạng thái edit, xóa theo index
        return (b.splitIndex || 0) - (a.splitIndex || 0);
      });

      const itemsToRemove = sortedItems
        .slice(newQuantity)
        .map(item => item.id);

      return orders.filter(item => !itemsToRemove.includes(item.id));
    }
  }

  /**
   * Delete một split item
   */
  static deleteSplitItem(
    itemId: string,
    orders: SplitServiceItem[]
  ): SplitServiceItem[] {
    const item = orders.find(o => o.id === itemId);
    if (!item || !item.splitGroupId) {
      // Không phải split item → xóa bình thường
      return orders.filter(o => o.id !== itemId);
    }

    const groupItems = orders.filter(o => o.splitGroupId === item.splitGroupId);

    if (groupItems.length === 1) {
      // Chỉ còn 1 item trong group → xóa luôn
      return orders.filter(o => o.id !== itemId);
    }

    // Còn nhiều items → xóa item này và reindex
    return orders
      .filter(o => o.id !== itemId)
      .map(o => {
        if (o.splitGroupId === item.splitGroupId && o.splitIndex && item.splitIndex && o.splitIndex > item.splitIndex) {
          return {
            ...o,
            splitIndex: o.splitIndex - 1,
          };
        }
        return o;
      });
  }

  /**
   * Update obs cho split items
   * Chỉ apply cho items chưa có obs riêng
   */
  static updateObsForGroup(
    splitGroupId: string,
    obsValue: any,
    orders: SplitServiceItem[]
  ): SplitServiceItem[] {
    return orders.map(item => {
      if (item.splitGroupId === splitGroupId && !item.obs) {
        return {
          ...item,
          obs: obsValue,
        };
      }
      return item;
    });
  }

  /**
   * Clean data trước khi gửi lên backend
   * Xóa các fields metadata client-side (UUID, split tracking, popup metadata, etc.)
   *
   * @param orders - Array orders từ form
   * @returns Array orders đã clean, ready để gửi API
   */
  static cleanForBackend(orders: SplitServiceItem[]): any[] {
    return orders.map(item => {
      // Destructure để loại bỏ metadata fields
      const {
        id,                    // ← Service ID, extracted separately for termId mapping
        _clientId,             // ← Client-side tracking ID for React keys
        splitGroupId,          // ← Client metadata, không gửi
        splitIndex,            // ← Client metadata, không gửi
        isManuallyEdited,      // ← Client tracking, không gửi
        editedFields,          // ← Client tracking, không gửi
        _splitItems,           // ← Popup storage, không gửi
        _hasSplitItems,        // ← Popup flag, không gửi
        ...cleanItem           // ← Giữ lại data thật
      } = item as any;

      return cleanItem;
    });
  }

  /**
   * Kiểm tra xem data có phải từ split feature không
   * Dùng để detect backward compatibility
   */
  static hasSplitMetadata(item: any): boolean {
    return !!(item.splitGroupId || item.splitIndex !== undefined);
  }

  static adjustSplitGroupQuantity(
    allOrders: SplitServiceItem[],
    splitGroupId: string,
    newQuantity: number
  ): SplitServiceItem[] {
    const groupItems = allOrders.filter(item => item.splitGroupId === splitGroupId);
    const otherItems = allOrders.filter(item => item.splitGroupId !== splitGroupId);
    const currentQuantity = groupItems.length;

    if (newQuantity === currentQuantity || newQuantity < 1) {
      return allOrders;
    }

    if (newQuantity > currentQuantity) {
      const itemsToAdd = newQuantity - currentQuantity;
      const templateItem = groupItems[0];

      const newItems = Array.from({ length: itemsToAdd }, (_, index) => ({
        ...templateItem,
        id: this.generateUUID(),
        splitIndex: currentQuantity + index + 1,
        isManuallyEdited: false,
        editedFields: [],
        obs: undefined,
      }));

      return [...otherItems, ...groupItems, ...newItems];
    }

    const itemsToRemove = currentQuantity - newQuantity;
    const editedItems = groupItems.filter(item => item.isManuallyEdited);
    const nonEditedItems = groupItems.filter(item => !item.isManuallyEdited);

    let itemsToKeep: SplitServiceItem[] = [];

    if (nonEditedItems.length >= itemsToRemove) {
      itemsToKeep = [
        ...editedItems,
        ...nonEditedItems.slice(0, nonEditedItems.length - itemsToRemove)
      ];
    } else {
      const remainingToRemove = itemsToRemove - nonEditedItems.length;
      itemsToKeep = editedItems.slice(0, editedItems.length - remainingToRemove);
    }

    return [
      ...otherItems,
      ...itemsToKeep.map((item, index) => ({
        ...item,
        splitIndex: index + 1
      }))
    ];
  }
}
